<script setup lang="ts">
import { watch } from 'vue'
import { useTranslate } from '@tolgee/vue'

import { BackgroundTheme, PLAN_TYPES } from '@/api/interfaces'
import { useEditorStore } from '@/stores/Editor'
import { useAccountStore } from '@/stores/Account'
import { getCSSVariable } from '@/helpers/utils'

import BaseRadioGroup, { type RadioItem } from '@/components/common/BaseRadioGroup.vue'
import EditorSettingsBackgroundThemeCustom from '@/components/editor/EditorSettingsBackgroundThemeCustom.vue'

const editorStore = useEditorStore()
const accountStore = useAccountStore()
const { t } = useTranslate('local')

const themes: RadioItem[] = [
  {
    label: t.value(`EditorBackgroundSettings.theme.light`),
    value: BackgroundTheme.LIGHT,
    imageUrl: '/src/assets/images/editor/background-light-theme.webp',
  },
  {
    label: t.value(`EditorBackgroundSettings.theme.dark`),
    value: BackgroundTheme.DARK,
    imageUrl: '/src/assets/images/editor/background-dark-theme.webp',
  },
  {
    label: t.value(`EditorBackgroundSettings.theme.custom.label`),
    value: BackgroundTheme.CUSTOM,
    disabled: accountStore.hasStarterPlan,
    planType: PLAN_TYPES.GROWTH,
    component: EditorSettingsBackgroundThemeCustom,
  },
]

const setDefaultThemeColors = () => {
  if (editorStore.editor.background.theme === BackgroundTheme.CUSTOM) {
    editorStore.editor.icon.style = 'flat'
    editorStore.editor.icon.impactColors.plastic.backgroundColor = '#FB337B'
    editorStore.editor.icon.impactColors.carbon.backgroundColor = '#582F8B'
    editorStore.editor.icon.impactColors.trees.backgroundColor = '#599A49'
    editorStore.editor.icon.impactColors.plastic.textColor = getCSSVariable('lightest')
    editorStore.editor.icon.impactColors.carbon.textColor = getCSSVariable('lightest')
    editorStore.editor.icon.impactColors.trees.textColor = getCSSVariable('lightest')
  } else {
    editorStore.editor.icon.style = 'simple'
    editorStore.editor.icon.impactColors.plastic.backgroundColor = '#1FAEF0'
    editorStore.editor.icon.impactColors.carbon.backgroundColor = '#FFB339'
    editorStore.editor.icon.impactColors.trees.backgroundColor = '#49E2A7'
    editorStore.editor.icon.impactColors.plastic.textColor = getCSSVariable('ui-black')
    editorStore.editor.icon.impactColors.carbon.textColor = getCSSVariable('ui-black')
    editorStore.editor.icon.impactColors.trees.textColor = getCSSVariable('ui-black')
  }
}

watch(
  () => editorStore.editor.background.theme,
  (currentTheme) => {
    if (currentTheme === BackgroundTheme.LIGHT) {
      editorStore.editor.hero.image.textColor = getCSSVariable('ui-black')
      editorStore.editor.hero.image.overlayColor = getCSSVariable('lightest')
      setDefaultThemeColors()
    }

    if (currentTheme === BackgroundTheme.DARK) {
      editorStore.editor.hero.image.textColor = getCSSVariable('lightest')
      editorStore.editor.hero.image.overlayColor = getCSSVariable('ui-black')
      setDefaultThemeColors()
    }

    if (currentTheme === BackgroundTheme.CUSTOM) {
      editorStore.editor.hero.image.textColor = getCSSVariable('lightest')
      editorStore.editor.hero.image.overlayColor =
        editorStore.editor.background.themeColor.background
      setDefaultThemeColors()
    }
  },
)
</script>

<template>
  <div class="mb-5">
    <BaseRadioGroup
      v-model="editorStore.editor.background.theme"
      :items="themes"
      :disabled="editorStore.publishLoading"
    />
  </div>
</template>

<style lang="scss" scoped></style>
